'use strict';
/**
 * Main Product V1 Section JavaScript
 * Custom Elements for gallery, plan CTA, accordion, and selling plans
 */

/* ========================================================================== 
   Gallery Component
   ========================================================================== */

if (!customElements.get('mpv1-gallery')) {
  class Mpv1Gallery extends HTMLElement {
    constructor() {
      super();
      this.currentIndex = 0;
      this.thumbnails = [];
      this.touchStartX = 0;
      this.touchEndX = 0;
    }

    connectedCallback() {
      this.mainImage = this.querySelector('.mpv1-gallery__main-img');
      this.thumbnails = Array.from(this.querySelectorAll('.mpv1-gallery__thumb'));
      this.prevBtn = this.querySelector('[data-gallery-prev]');
      this.nextBtn = this.querySelector('[data-gallery-next]');
      this.mainWrapper = this.querySelector('.mpv1-gallery__main');
      this.thumbsContainer = this.querySelector('.mpv1-gallery__thumbs');

      if (!this.mainImage || this.thumbnails.length === 0) return;

      // Find initial active thumbnail
      this.currentIndex = this.thumbnails.findIndex((thumb) => thumb.classList.contains('is-active'));
      if (this.currentIndex === -1) this.currentIndex = 0;

      this.bindEvents();
      this.updateNavButtons();
    }

    bindEvents() {
      // Thumbnail clicks
      this.thumbnails.forEach((thumb, index) => {
        thumb.addEventListener('click', () => this.goToSlide(index));
      });

      // Navigation buttons
      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', () => this.prev());
      }

      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', () => this.next());
      }

      // Keyboard navigation
      this.addEventListener('keydown', (e) => this.handleKeydown(e));

      // Touch/swipe support on main image wrapper
      if (this.mainWrapper) {
        this.mainWrapper.addEventListener('touchstart', (e) => this.handleTouchStart(e), { passive: true });
        this.mainWrapper.addEventListener('touchend', (e) => this.handleTouchEnd(e), { passive: true });
      }
    }

    handleKeydown(e) {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.prev();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.next();
      }
    }

    handleTouchStart(e) {
      this.touchStartX = e.changedTouches[0].screenX;
    }

    handleTouchEnd(e) {
      this.touchEndX = e.changedTouches[0].screenX;
      this.handleSwipe();
    }

    handleSwipe() {
      const swipeThreshold = 50;
      const diff = this.touchStartX - this.touchEndX;

      if (Math.abs(diff) < swipeThreshold) return;

      if (diff > 0) {
        // Swipe left - go next
        this.next();
      } else {
        // Swipe right - go prev
        this.prev();
      }
    }

    prev() {
      if (this.currentIndex > 0) {
        this.goToSlide(this.currentIndex - 1);
      }
    }

    next() {
      if (this.currentIndex < this.thumbnails.length - 1) {
        this.goToSlide(this.currentIndex + 1);
      }
    }

    goToSlide(index) {
      if (index < 0 || index >= this.thumbnails.length) return;

      const thumb = this.thumbnails[index];
      const fullSrc = thumb.dataset.full;
      const srcset = thumb.dataset.srcset;
      const alt = thumb.dataset.alt || '';

      if (!fullSrc) return;

      // Update main image — set srcset before src to avoid a double network
      // request: if src fires first the browser starts fetching the 1200px
      // image, then cancels/restarts when srcset is evaluated a tick later.
      this.mainImage.classList.add('is-loading');

      const onLoad = () => {
        this.mainImage.classList.remove('is-loading');
        this.mainImage.removeEventListener('load', onLoad);
      };

      this.mainImage.addEventListener('load', onLoad);

      if (srcset) {
        this.mainImage.srcset = srcset;
      }

      this.mainImage.src = fullSrc;
      this.mainImage.alt = alt;

      // Update active states
      this.thumbnails.forEach((t, i) => {
        t.classList.toggle('is-active', i === index);
        t.setAttribute('aria-current', i === index ? 'true' : 'false');
      });

      this.currentIndex = index;
      this.updateNavButtons();

      // Scroll active thumbnail into view — scroll the strip container directly
      // to avoid scrollIntoView bubbling up to the viewport and shifting the layout.
      if (this.thumbsContainer) {
        const scrollLeft = thumb.offsetLeft - (this.thumbsContainer.offsetWidth / 2) + (thumb.offsetWidth / 2);
        this.thumbsContainer.scrollTo({ left: scrollLeft, behavior: 'smooth' });
      }
    }

    updateNavButtons() {
      if (this.prevBtn) {
        this.prevBtn.disabled = this.currentIndex === 0;
        this.prevBtn.setAttribute('aria-disabled', this.currentIndex === 0 ? 'true' : 'false');
      }

      if (this.nextBtn) {
        this.nextBtn.disabled = this.currentIndex === this.thumbnails.length - 1;
        this.nextBtn.setAttribute('aria-disabled', this.currentIndex === this.thumbnails.length - 1 ? 'true' : 'false');
      }
    }
  }

  customElements.define('mpv1-gallery', Mpv1Gallery);
}

/* ========================================================================== 
   Pack Selector Component
   Quantity-based bundle picker — single variant, no selling plans.
   Each card has its own ORDER NOW button. Clicking it directly adds that
   plan's quantity to cart and redirects to checkout.
   Passes supply_plan + rebill_days as line item properties so the
   external subscription platform (Phoenix / Checkout Champ) can read
   them at checkout.
   ========================================================================== */

if (!customElements.get('mpv1-pack-selector')) {
  class Mpv1PackSelector extends HTMLElement {
    constructor() {
      super();
      this.loadingBtn = null;
      this.errorEl = null;
      this.layoutType = null;
    }

    connectedCallback() {
      this.variantId = this.dataset.variantId;
      this.layoutType = this.dataset.layout || 'cards';
      this.errorEl = this.querySelector('.mpv1-pack__error');

      if (!this.variantId) return;

      if (this.layoutType === 'switchers') {
        this._initSwitchers();
      } else {
        this._initCards();
      }
    }

    _initCards() {
      this.cards = Array.from(this.querySelectorAll('.mpv1-pack'));
      if (this.cards.length === 0) return;

      // Each card has its own CTA button — bind per-card
      this.cards.forEach((card) => {
        const btn = card.querySelector('.mpv1-pack__cta-btn');

        if (btn) {
          btn.addEventListener('click', (e) => {
            e.stopPropagation();
            this._handleAddToCartFromCard(card, btn);
          });
        }
      });
    }

    _initSwitchers() {
      this.radios = Array.from(this.querySelectorAll('.mpv1-switcher__radio'));
      this.switcherBtn = this.querySelector('.mpv1-switchers__cta-btn');
      this.switcherBtnText = this.querySelector('.mpv1-switchers__cta-text');
      this.switcherBtnPrice = this.querySelector('.mpv1-switchers__cta-price');
      this.switcherBtnCurrent = this.querySelector('.mpv1-switchers__cta-current');
      this.switcherBtnCompare = this.querySelector('.mpv1-switchers__cta-compare');
      this.baseButtonText = this.switcherBtnText ? this.switcherBtnText.textContent.trim() : 'ADD TO CART';
      this.onetimeLink = this.querySelector('[data-onetime-link]');
      this.onetimePrice = this.querySelector('.mpv1-switcher__onetime-price');
      this.addonCheckbox = this.querySelector('[data-addon-checkbox]');
      this.addonPriceSrc = this.querySelector('.mpv1-addon__price-src');

      // NEW bank/compliance setup: optional subscription opt-in checkbox.
      // Old templates do not render this checkbox, so their behavior stays unchanged.
      this.subscriptionOptinCheckbox = this.querySelector('[data-subscription-optin-checkbox]');
      this.subscriptionOptin = this.querySelector('.mpv1-subscription-optin');

      if (this.radios.length === 0 || !this.switcherBtn) return;

      // Select default option
      const defaultOption = this.querySelector('[data-default] .mpv1-switcher__radio');

      if (defaultOption) {
        defaultOption.checked = true;
      } else if (this.radios[0]) {
        this.radios[0].checked = true;
      }

      // Preserve the ORIGINAL subscription values before any bank/compliance
      // opt-in code swaps the live data attributes to one-time values.
      //
      // This makes the same JS work in both modes:
      // - OLD setup: separate one-time purchase link, no opt-in checkbox.
      // - NEW setup: one-time is default and subscription is an unchecked opt-in.
      if (this.subscriptionOptinCheckbox) {
        this.radios.forEach((radio) => {
          const label = radio.closest('label');
          if (!label) return;

          if (!label.dataset.subscriptionCheckoutUrl) {
            label.dataset.subscriptionCheckoutUrl = label.dataset.checkoutUrl || '';
          }

          if (!label.dataset.subscriptionTotalPrice) {
            label.dataset.subscriptionTotalPrice = label.dataset.totalPrice || '';
          }

          if (!label.dataset.subscriptionComparePrice) {
            label.dataset.subscriptionComparePrice = label.dataset.comparePrice || '';
          }
        });

        // Bank/compliance requirement: one-time purchase is the default.
        this.subscriptionOptinCheckbox.checked = false;
        this._syncSubscriptionOptinPlans();
      }

      // Render price now; re-sync if BucksCC converts .money spans later (loads async)
      this._syncAfterCurrencyConversion();

      // Update selection state on radio change
      this.radios.forEach((radio) => {
        radio.addEventListener('change', () => {
          if (this.subscriptionOptinCheckbox) {
            this._syncSubscriptionOptinPlans();
          }

          this._updateSwitcherSelection();
        });
      });

      // Re-render CTA total when the add-on opt-in is toggled
      if (this.addonCheckbox) {
        this.addonCheckbox.addEventListener('change', () => {
          this._updateSwitcherSelection();
        });
      }

      // NEW setup only: whenever the customer opts in/out of subscription,
      // re-render the CTA from the correct mode-specific price + URL.
      // No checkbox = old setup = no behavior change.
      if (this.subscriptionOptinCheckbox) {
        this.subscriptionOptinCheckbox.addEventListener('change', () => {
          this._syncSubscriptionOptinPlans();
          this._updateSwitcherSelection();
        });
      }
    }

    _syncSubscriptionOptinPlans() {
      if (!this.subscriptionOptinCheckbox) return;

      const subscriptionMode = this.subscriptionOptinCheckbox.checked === true;

      this.radios.forEach((radio) => {
        const label = radio.closest('label');
        if (!label) return;

        const purchaseData = this._getSwitcherPurchaseData(label);
        const totalPriceEl = label.querySelector('.mpv1-switcher__total-amount');
        const comparePriceEl = label.querySelector('.mpv1-switcher__compare');

        // Keep the live data attributes in the active mode so any existing
        // integrations reading them still receive the correct URL/price.
        label.dataset.checkoutUrl = purchaseData.checkoutUrl || '#';
        label.dataset.totalPrice = purchaseData.totalPrice || '';
        label.dataset.comparePrice = purchaseData.comparePrice || '';

        if (totalPriceEl && purchaseData.totalPrice) {
          totalPriceEl.textContent = purchaseData.totalPrice;
        }

        if (comparePriceEl) {
          if (purchaseData.comparePrice) {
            comparePriceEl.textContent = purchaseData.comparePrice;
            comparePriceEl.hidden = false;
            comparePriceEl.style.removeProperty('display');
          } else {
            comparePriceEl.hidden = true;
          }
        }
      });

      if (this.subscriptionOptin) {
        this.subscriptionOptin.classList.toggle('is-subscription-selected', subscriptionMode);
        this.subscriptionOptin.dataset.purchaseMode = subscriptionMode ? 'subscription' : 'onetime';
      }
    }

    _syncAfterCurrencyConversion() {
      // Render immediately from the server-rendered prices so the CTA never
      // sits empty waiting for the currency app.
      this._updateSwitcherSelection();

      const selectedRadio = this.querySelector('.mpv1-switcher__radio:checked');
      const label = selectedRadio?.closest('label');
      const totalPriceEl = label?.querySelector('.mpv1-switcher__total-amount');

      if (!totalPriceEl) return;

      // If BucksCC already converted (text differs from the raw USD data
      // attribute), the render above picked up the converted value — done.
      const usdText = label.dataset.totalPrice || '';
      const currentText = totalPriceEl.textContent.trim();

      if (currentText && currentText !== usdText) return;

      // Otherwise re-sync once when BucksCC mutates the span; stop watching
      // after a grace period when no conversion is coming.
      const observer = new MutationObserver(() => {
        observer.disconnect();
        this._updateSwitcherSelection();
      });

      observer.observe(totalPriceEl, {
        childList: true,
        subtree: true,
        characterData: true
      });

      setTimeout(() => observer.disconnect(), 5000);
    }

    _getSwitcherPurchaseData(label) {
      if (!label) {
        return {
          checkoutUrl: '',
          totalPrice: '',
          comparePrice: ''
        };
      }

      const totalPriceEl = label.querySelector('.mpv1-switcher__total-amount');
      const comparePriceEl = label.querySelector('.mpv1-switcher__compare');

      // OLD SETUP
      // No subscription opt-in checkbox exists, so preserve the original logic
      // exactly: read the visible/currency-converted DOM first, then data attrs.
      if (!this.subscriptionOptinCheckbox) {
        return {
          checkoutUrl: label.dataset.checkoutUrl || '',
          totalPrice: totalPriceEl?.textContent?.trim() || label.dataset.totalPrice || '',
          comparePrice: comparePriceEl?.textContent?.trim() || label.dataset.comparePrice || ''
        };
      }

      // NEW BANK/COMPLIANCE SETUP
      // Unchecked = one-time. Checked = subscription.
      const subscriptionMode = this.subscriptionOptinCheckbox.checked === true;

      if (subscriptionMode) {
        const subscriptionPrice =
          label.dataset.subscriptionTotalPrice ||
          label.dataset.totalPrice ||
          '';

        const subscriptionCompare =
          label.dataset.subscriptionComparePrice ||
          label.dataset.comparePrice ||
          '';

        const subscriptionUrl =
          label.dataset.subscriptionCheckoutUrl ||
          label.dataset.checkoutUrl ||
          '';

        return {
          checkoutUrl: subscriptionUrl,
          totalPrice: subscriptionPrice,
          comparePrice: subscriptionCompare
        };
      }

      // One-time mode. The hidden source span is preferred because BucksCC can
      // convert it too; otherwise use the configured one-time price setting.
      const oneTimePriceEl = label.querySelector('.mpv1-switcher__onetime-price-src');

      const oneTimePrice =
        oneTimePriceEl?.textContent?.trim() ||
        label.dataset.onetimePrice ||
        label.dataset.subscriptionTotalPrice ||
        label.dataset.totalPrice ||
        '';

      return {
        checkoutUrl: label.dataset.onetimeUrl || '',
        totalPrice: oneTimePrice,
        // Keep the configured compare-at price visible in one-time mode too.
        comparePrice:
          label.dataset.subscriptionComparePrice ||
          label.dataset.comparePrice ||
          ''
      };
    }

    _updateSwitcherSelection() {
      if (!this.switcherBtnText) return;

      // Find selected radio and get the active purchase-mode data.
      const selectedRadio = this.querySelector('.mpv1-switcher__radio:checked');
      if (!selectedRadio) return;

      const label = selectedRadio.closest('label');
      const purchaseData = this._getSwitcherPurchaseData(label);
      const checkoutUrl = purchaseData.checkoutUrl;
      const totalPrice = purchaseData.totalPrice;
      const comparePrice = purchaseData.comparePrice;

      // Add-on opt-in checked → CTA shows plan total + add-on price.
      // For the NEW bank setup, only use subscription add-on checkout URLs while
      // subscription mode is actually checked. Old setup stays unchanged.
      const addonPrice = this._getAddonPrice();

      const displayPrice = addonPrice > 0 && totalPrice
        ? this._formatMoney(this._parseMoney(totalPrice) + addonPrice, totalPrice)
        : totalPrice;

      const hasDetailedPricing = !!(this.switcherBtnCurrent || this.switcherBtnCompare);

      if (hasDetailedPricing) {
        // Detailed layout: label stays fixed, price/compare-at live in their own spans.
        this.switcherBtnText.textContent = this.baseButtonText;

        if (this.switcherBtnCurrent) {
          this.switcherBtnCurrent.textContent = displayPrice ? ` | ${displayPrice}` : '';
        }

        if (this.switcherBtnCompare) {
          if (comparePrice) {
            this.switcherBtnCompare.textContent = comparePrice;
            this.switcherBtnCompare.hidden = false;
          } else {
            this.switcherBtnCompare.hidden = true;
          }
        }
      } else if (this.switcherBtnPrice) {
        // Update button text to show: "ADD TO CART - $149.70".
        this.switcherBtnText.textContent = this.baseButtonText;
        this.switcherBtnPrice.textContent = displayPrice ? ` - ${displayPrice}` : '';
      } else if (displayPrice) {
        this.switcherBtnText.textContent = `${this.baseButtonText} - ${displayPrice}`;
      } else {
        this.switcherBtnText.textContent = this.baseButtonText;
      }

      // Update anchor href.
      const newSubscriptionMode =
        !!this.subscriptionOptinCheckbox &&
        this.subscriptionOptinCheckbox.checked === true;

      const allowAddonCheckout =
        !this.subscriptionOptinCheckbox || newSubscriptionMode;

      const addonUrl =
        allowAddonCheckout && this.addonCheckbox?.checked
          ? label?.dataset.addonCheckoutUrl || ''
          : '';

      const targetUrl = addonUrl || checkoutUrl;

      if (this.switcherBtn) {
        if (targetUrl && targetUrl !== '#') {
          this.switcherBtn.href =
            typeof window.upDecorateChampCheckoutUrl === 'function'
              ? window.upDecorateChampCheckoutUrl(targetUrl)
              : typeof upDecorateChampCheckoutUrl === 'function'
                ? upDecorateChampCheckoutUrl(targetUrl)
                : targetUrl;

          this.switcherBtn.removeAttribute('aria-disabled');
        } else {
          this.switcherBtn.href = '#';
          this.switcherBtn.setAttribute('aria-disabled', 'true');
        }
      }

      // OLD setup only: keep the separate one-time purchase row synced to the
      // selected plan. In the NEW setup that row is not rendered.
      if (label && this.onetimeLink) {
        const otPriceEl = label.querySelector('.mpv1-switcher__onetime-price-src');
        const otPrice = otPriceEl?.textContent?.trim() || label.dataset.onetimePrice;
        const otUrl = label.dataset.onetimeUrl;

        if (this.onetimePrice && otPrice) {
          this.onetimePrice.textContent = otPrice;
        }

        if (otUrl) {
          this.onetimeLink.href =
            typeof window.upDecorateChampCheckoutUrl === 'function'
              ? window.upDecorateChampCheckoutUrl(otUrl)
              : typeof upDecorateChampCheckoutUrl === 'function'
                ? upDecorateChampCheckoutUrl(otUrl)
                : otUrl;
        }
      }

      this._updateGalleryImage(label);
    }

    /**
     * Swap the first media-gallery image to the selected plan's image
     * (plan_N_gallery_image setting). Plans without an image restore the
     * product's original first image. No-op when the section has no gallery
     * or no plan defines a gallery image.
     */
    _updateGalleryImage(label) {
      if (!label) return;

      const gallery = this.closest('.mpv1')?.querySelector('mpv1-gallery');
      const mainImage = gallery?.querySelector('img.mpv1-gallery__main-img');

      if (!gallery || !mainImage) return;

      const firstThumb = gallery.querySelector('.mpv1-gallery__thumb');
      const thumbImg = firstThumb?.querySelector('img');

      // Snapshot the product's original first image once, so plans without
      // a custom gallery image can restore it after a swap
      if (!this._originalGallery) {
        if (!label.dataset.galleryImage) return; // nothing to swap yet — keep defaults

        this._originalGallery = {
          src: firstThumb?.dataset.full || mainImage.getAttribute('src') || '',
          srcset: firstThumb?.dataset.srcset || mainImage.getAttribute('srcset') || '',
          alt: firstThumb?.dataset.alt || mainImage.alt || '',
          thumbSrc: thumbImg?.getAttribute('src') || '',
          thumbSrcset: thumbImg?.getAttribute('srcset') || ''
        };
      }

      const hasPlanImage = Boolean(label.dataset.galleryImage);
      const src = hasPlanImage ? label.dataset.galleryImage : this._originalGallery.src;
      const srcset = hasPlanImage ? (label.dataset.gallerySrcset || '') : this._originalGallery.srcset;
      const alt = hasPlanImage ? (label.dataset.galleryAlt || '') : this._originalGallery.alt;
      const thumbSrc = hasPlanImage ? (label.dataset.galleryThumb || src) : this._originalGallery.thumbSrc;

      if (!src) return;

      // Keep the first thumbnail in sync so gallery navigation returns to the
      // plan image instead of the product default
      if (firstThumb) {
        firstThumb.dataset.full = src;

        if (srcset) {
          firstThumb.dataset.srcset = srcset;
        } else {
          delete firstThumb.dataset.srcset;
        }

        firstThumb.dataset.alt = alt;

        if (thumbImg && thumbSrc) {
          // Swap the visible thumbnail image too. The srcset must be replaced
          // (not just src) — browsers resolve srcset over src, so a stale
          // srcset would keep showing the old image.
          if (hasPlanImage) {
            thumbImg.removeAttribute('srcset');
          } else if (this._originalGallery.thumbSrcset) {
            thumbImg.setAttribute('srcset', this._originalGallery.thumbSrcset);
          }

          thumbImg.src = thumbSrc;
        }
      }

      // Jump to the first slide so the new image is visible; the gallery
      // component re-reads the thumb data we just updated
      if (firstThumb && typeof gallery.goToSlide === 'function') {
        gallery.goToSlide(0);
      } else {
        if (srcset) {
          mainImage.srcset = srcset;
        } else {
          mainImage.removeAttribute('srcset');
        }

        mainImage.src = src;
        mainImage.alt = alt;
      }
    }

    async _handleAddToCartFromCard(card, btn) {
      if (btn.classList.contains('is-loading')) return;

      this._clearError();
      this._setLoading(btn, true);

      const qty = parseInt(card.dataset.qty, 10) || 1;
      const supplyPlan = card.dataset.supplyPlan || '';
      const rebillDays = card.dataset.rebillDays || '';

      await this._addToCart(qty, supplyPlan, rebillDays, btn);
    }

    async _handleAddToCartFromSwitcher(btn) {
      if (btn.classList.contains('is-loading')) return;

      // Find selected radio
      const selectedRadio = this.querySelector('.mpv1-switcher__radio:checked');

      if (!selectedRadio) {
        this._showError('Please select a supply option');
        return;
      }

      this._clearError();
      this._setLoading(btn, true);

      // Get data from parent label
      const label = selectedRadio.closest('label');
      const qty = parseInt(label.dataset.qty, 10) || 1;
      const supplyPlan = label.dataset.supplyPlan || '';
      const rebillDays = label.dataset.rebillDays || '';

      await this._addToCart(qty, supplyPlan, rebillDays, btn);
    }

    async _addToCart(qty, supplyPlan, rebillDays, btn) {
      const cartItem = {
        id: parseInt(this.variantId, 10),
        quantity: qty,
        properties: {}
      };

      // Only attach properties that have values so the cart stays clean
      if (supplyPlan) cartItem.properties['_supply_plan'] = supplyPlan;
      if (rebillDays) cartItem.properties['_rebill_days'] = rebillDays;

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({ items: [cartItem] })
        });

        if (!response.ok) {
          const err = await response.json();
          throw new Error(err.description || 'Could not add to cart');
        }

        const data = await response.json();

        // Notify GTM / GA4 listeners
        document.dispatchEvent(new CustomEvent('cart:item:added', {
          bubbles: true,
          detail: {
            items: data.items || data,
            variantId: cartItem.id,
            quantity: qty,
            supplyPlan,
            rebillDays
          }
        }));

        this._setLoading(btn, false);
        window.updateCartDrawer();
      } catch (err) {
        console.error('[mpv1-pack-selector] Add to cart error:', err);
        this._setLoading(btn, false);
        this._showError(err.message || 'Something went wrong. Please try again.');
      }
    }

    _setLoading(btn, loading) {
      btn.classList.toggle('is-loading', loading);
      btn.setAttribute('aria-busy', loading ? 'true' : 'false');
      this.loadingBtn = loading ? btn : null;
    }

    _showError(message) {
      if (this.errorEl) {
        this.errorEl.textContent = message;
        this.errorEl.hidden = false;
      }
    }

    _clearError() {
      if (this.errorEl) this.errorEl.hidden = true;
    }

    // Add-on price as a number, 0 unless the opt-in checkbox is checked.
    // Reads the hidden BucksCC-converted span first; falls back to the raw
    // data attribute so the math stays in the displayed currency.
    _getAddonPrice() {
      if (!this.addonCheckbox?.checked) return 0;

      const priceStr =
        this.addonPriceSrc?.textContent?.trim() ||
        this.addonCheckbox.dataset.addonPrice ||
        '';

      return this._parseMoney(priceStr);
    }

    _parseMoney(str) {
      if (!str) return 0;

      const cleaned = str.replace(/[^0-9.,]/g, '');
      if (!cleaned) return 0;

      const lastComma = cleaned.lastIndexOf(',');
      const lastDot = cleaned.lastIndexOf('.');

      if (lastComma > lastDot) {
        // European format: 1.234,56 → comma is decimal
        return parseFloat(cleaned.replace(/\./g, '').replace(',', '.')) || 0;
      }

      return parseFloat(cleaned.replace(/,/g, '')) || 0;
    }

    // Formats mirroring the reference string's locale (BucksCC may convert
    // prices to European format, e.g. $97.136,98) so the sum reads like the
    // surrounding prices.
    _formatMoney(amount, referenceStr) {
      const symbol = (referenceStr.match(/^[^\d\s]+/) || ['$'])[0];
      const cleaned = referenceStr.replace(/[^0-9.,]/g, '');
      const european = cleaned.lastIndexOf(',') > cleaned.lastIndexOf('.');
      const [intPart, decPart] = amount.toFixed(amount % 1 === 0 ? 0 : 2).split('.');
      const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, european ? '.' : ',');

      return symbol + grouped + (decPart ? (european ? ',' : '.') + decPart : '');
    }
  }

  customElements.define('mpv1-pack-selector', Mpv1PackSelector);
}

/* ========================================================================== 
   Accordion Component
   ========================================================================== */

if (!customElements.get('mpv1-accordion')) {
  class Mpv1Accordion extends HTMLElement {
    constructor() {
      super();
      this.isOpen = false;
    }

    connectedCallback() {
      this.trigger = this.querySelector('.mpv1-accordion__trigger');
      this.panel = this.querySelector('.mpv1-accordion__panel');
      this.content = this.querySelector('.mpv1-accordion__content');

      if (!this.trigger || !this.panel) return;

      // Set initial ARIA attributes
      const triggerId = `mpv1-accordion-trigger-${this.generateId()}`;
      const panelId = `mpv1-accordion-panel-${this.generateId()}`;

      this.trigger.setAttribute('id', triggerId);
      this.trigger.setAttribute('aria-controls', panelId);
      this.trigger.setAttribute('aria-expanded', 'false');
      this.panel.setAttribute('id', panelId);
      this.panel.setAttribute('aria-labelledby', triggerId);
      this.panel.setAttribute('role', 'region');

      this.trigger.addEventListener('click', () => this.toggle());

      // Support keyboard
      this.trigger.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.toggle();
        }
      });
    }

    generateId() {
      return Math.random().toString(36).substring(2, 9);
    }

    toggle() {
      this.isOpen = !this.isOpen;
      this.classList.toggle('is-open', this.isOpen);
      this.trigger.setAttribute('aria-expanded', this.isOpen ? 'true' : 'false');

      if (this.content) {
        if (this.isOpen) {
          // Measure actual content height before opening so the CSS transition
          // animates to the correct pixel value (P1 fix: stale height on close).
          const height = this.content.scrollHeight;
          this.style.setProperty('--mpv1-accordion-height', `${height}px`);
        } else {
          // Reset the height var on close so a subsequent open re-measures correctly.
          this.style.removeProperty('--mpv1-accordion-height');
        }
      }
    }

    open() {
      if (!this.isOpen) this.toggle();
    }

    close() {
      if (this.isOpen) this.toggle();
    }
  }

  customElements.define('mpv1-accordion', Mpv1Accordion);
}

/* ========================================================================== 
   Resilia Pack Selector Component
   Resilia-style plan picker: horizontal card rows with radio selection.
   Clicking a card updates the ATC button price text and checkout URL.
   ========================================================================== */

if (!customElements.get('mpv1-pack-selector-resilia')) {
  class Mpv1PackSelectorResilia extends HTMLElement {
    connectedCallback() {
      this.radios = Array.from(this.querySelectorAll('.mpv1-rpp__radio'));
      this.atcBtn = this.querySelector('.mpv1-rpp__atc-btn');
      this.priceText = this.querySelector('.mpv1-rpp__atc-price-text');
      this.compareEl = this.querySelector('.mpv1-rpp__atc-compare');
      this.barPrice = this.querySelector('.mpv1-rpp__price-bar-price');
      this.barCompare = this.querySelector('.mpv1-rpp__price-bar-compare');
      this.barBadge = this.querySelector('.mpv1-rpp__price-bar-badge');
      this.errorEl = this.querySelector('.mpv1-rpp__error');

      if (this.radios.length === 0) return;

      // Ensure default radio is checked
      const defaultCard = this.querySelector('[data-default] .mpv1-rpp__radio');

      if (defaultCard && !this.querySelector('.mpv1-rpp__radio:checked')) {
        defaultCard.checked = true;
      }

      // Render price now; re-sync if BucksCC converts .money spans later (loads async)
      this._syncAfterCurrencyConversion();

      this.radios.forEach((radio) => {
        radio.addEventListener('change', () => this._updateSelection());
      });
    }

    _syncAfterCurrencyConversion() {
      // Render immediately from the server-rendered prices so the CTA never
      // sits empty waiting for the currency app.
      this._updateSelection();

      const checked = this.querySelector('.mpv1-rpp__radio:checked');
      const card = checked?.closest('label');
      const priceEl = card?.querySelector('.mpv1-rpp__price');

      if (!priceEl) return;

      // If BucksCC already converted (text differs from the raw USD data
      // attribute), the render above picked up the converted value — done.
      const usdText = card.dataset.totalPrice || '';
      const currentText = priceEl.textContent.trim();

      if (currentText && currentText !== usdText) return;

      // Otherwise re-sync once when BucksCC mutates the span; stop watching
      // after a grace period when no conversion is coming.
      const observer = new MutationObserver(() => {
        observer.disconnect();
        this._updateSelection();
      });

      observer.observe(priceEl, {
        childList: true,
        subtree: true,
        characterData: true
      });

      setTimeout(() => observer.disconnect(), 5000);
    }

    _updateSelection() {
      const checked = this.querySelector('.mpv1-rpp__radio:checked');
      if (!checked) return;

      const card = checked.closest('label');

      // Read price from the BucksCC-converted DOM elements; fall back to data attributes
      const priceEl = card?.querySelector('.mpv1-rpp__price');
      const compareEl = card?.querySelector('.mpv1-rpp__compare');
      const totalPrice = priceEl?.textContent?.trim() || card?.dataset.totalPrice || '';
      const comparePrice = compareEl?.textContent?.trim() || card?.dataset.comparePrice || '';
      const checkoutUrl = card?.dataset.checkoutUrl ?? '#';

      // ATC button price text
      if (this.priceText) {
        this.priceText.textContent = totalPrice ? ` | ${totalPrice}` : '';
      }

      // ATC button strikethrough
      if (this.compareEl) {
        if (comparePrice) {
          this.compareEl.textContent = comparePrice;
          this.compareEl.hidden = false;
        } else {
          this.compareEl.hidden = true;
        }
      }

      // ATC button href + aria-label
      if (this.atcBtn) {
        this.atcBtn.href = upDecorateChampCheckoutUrl(checkoutUrl);
        const btnLabel = this.atcBtn.querySelector('.mpv1-rpp__atc-label')?.textContent?.trim() ?? 'Add To Cart';
        this.atcBtn.setAttribute('aria-label', totalPrice ? `${btnLabel} – ${totalPrice}` : btnLabel);
      }

      // Price bar price
      if (this.barPrice && totalPrice) {
        this.barPrice.textContent = totalPrice;
      }

      // Price bar compare
      if (this.barCompare) {
        if (comparePrice) {
          this.barCompare.textContent = comparePrice;
          this.barCompare.hidden = false;
        } else {
          this.barCompare.hidden = true;
        }
      }

      // Price bar badge — savings = compare - price, prefixed with badge label text
      if (this.barBadge) {
        if (comparePrice && totalPrice) {
          const savings = this._parseMoney(comparePrice) - this._parseMoney(totalPrice);

          if (savings > 0) {
            const prefix = (this.barBadge.dataset.prefix ?? '').trim();
            this.barBadge.textContent = prefix
              ? `${prefix} ${this._formatMoney(savings, totalPrice)}`
              : this._formatMoney(savings, totalPrice);
            this.barBadge.hidden = false;
          } else {
            this.barBadge.hidden = true;
          }
        } else {
          this.barBadge.hidden = true;
        }
      }
    }

    _parseMoney(str) {
      if (!str) return 0;

      const cleaned = str.replace(/[^0-9.,]/g, '');
      if (!cleaned) return 0;

      const lastComma = cleaned.lastIndexOf(',');
      const lastDot = cleaned.lastIndexOf('.');

      if (lastComma > lastDot) {
        // European format: 1.234,56 → comma is decimal
        return parseFloat(cleaned.replace(/\./g, '').replace(',', '.')) || 0;
      }

      return parseFloat(cleaned.replace(/,/g, '')) || 0;
    }

    _formatMoney(amount, referenceStr) {
      const symbol = (referenceStr.match(/^[^\d\s]+/) || ['$'])[0];
      return symbol + (amount % 1 === 0 ? amount.toFixed(0) : amount.toFixed(2));
    }
  }

  customElements.define('mpv1-pack-selector-resilia', Mpv1PackSelectorResilia);
}
